import { Utility } from 'helpers/UtilityHelpers';
import { Message } from 'constants/messages';
import { Code } from 'constants/codes';
import { apiClient } from 'services/tokenInterceptor';
import { MessageType, OptionType, Privilege, ReportFilter } from 'models/BaseModel';
import { Doctor } from 'models/DoctorModel'
import { User } from 'models/UserModel'
import { Client } from 'models/ClientModel';
import { City } from 'models/CityModel';
import { Company } from 'models/CompanyModel';
import { Location } from 'models/LocationModel';
import { NavigateFunction } from 'react-router-dom';
import { Name } from 'constants/fieldname';
import { Dispatch, SetStateAction } from 'react';
import { RecDoctor } from 'models/RecDoctorModel';
import { Item } from 'models/ItemModel';
import { Inventory } from 'models/InventoryModel';
import { ItemInventoryGroup } from 'models/ItemInventoryGroup';
import { Extract } from 'helpers/ExtractHelper';
import { Category } from 'models/CategoryModel';
import { Product } from 'models/ProductModel';
import { Offer } from 'models/OfferModel';
import { Document } from 'models/DocumentModel';
import { SmsRequest } from 'models/BaseModel';
import { getPdfFile } from 'components/common/SaveToPdf';
import { RefObject } from "react";
import { Files } from './FileHelpers';
import { Submitter } from 'models/SubmitterModel';
import { Insurer } from 'models/InsurerModel';
import { Tax } from 'models/TaxModel';
import { Insurance } from 'models/InsuranceModel';
import { Term } from 'models/TermModel';

//function to get data from the server
const getDataList = async <T>(
  url: string,
  setData: (data: T[]) => void,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void
): Promise<void> => {
  setLoading(true);
  setError(null);
  setData([]);

  try {
    const result = await getAllRecords(url);

    ////console.log("Page-List-Data:", result);
  
    if (result.code && result.code !== Message.Code.SUCCESS) {
      if (result.code === Message.Code.NOT_FOUND) {
        setError(Message.Title.NOT_FOUND);
      } else {
        setError(result.message || Message.Title.UNEXPECTED_ERROR);
      }
    } else if (Array.isArray(result.data)) {
      
      setData(result.data);
    } else {
      setError(Message.Title.UNEXPECTED_FORMAT);
    }
  } catch (error) {
    setError(Message.Title.FETCH_FAILED);
  } finally {
    setLoading(false);
  }
};

const returnDataList = async <T>(
  url: string,
  setError?: (error: string | null) => void,
  setLoading?: (loading: boolean) => void
): Promise<T[] | null> => {
  setLoading?.(true);
  setError?.(null);

  try {
    //console.log("returnDataList from API:", url);
    const response = await getAllRecords(url);
    //console.log("returnDataList Response:", response);

    // ✅ Handle expected format: { status, code, message, data }
    if (response && typeof response === "object") {
      if (response.code !== 200) {
        setError?.(response.message || "Unexpected response code.");
        return null;
      }

      if (Array.isArray(response.data)) {
        ////console.log("✅ Data received:", response.data);
        return response.data as T[];
      } else {
        setError?.("Unexpected response format: 'data' is not an array.");
        return null;
      }
    }

    // ❌ If response is invalid
    setError?.("Unexpected response format.");
    return null;

  } catch (err) {
    console.error("❌ Fetch failed:", err);
    setError?.("Failed to fetch data.");
    return null;

  } finally {
    setLoading?.(false);
  }
};

//fetch all records from the server
const getAllRecords = async (url: string) => {
  try {
    ////console.log("API---url--->",url);
    const response = await apiClient.get(url);
    ////console.log("API---response--->",response);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      ////console.log("API---error--->",error.response);
      const { status, data } = error.response;
      const errorMsg = `${Message.Title.ERROR_STATUS} ${status}. Details: ${JSON.stringify(data)}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    } else {
      console.error(Message.Title.FETCH_ERROR, error.message);
      throw error;
    }
  }
};

//get access token from local storage, if not valid, generate new access token
const getAccessToken = async () => {
 
  var accessToken = Utility.Storage.Get(Code.LocalKey.AccessToken);

  if (!accessToken) {
    console.error(Message.Title.TOKEN_NOT_FOUND);
    throw new Error(Message.Title.TOKEN_NOT_FOUND); 
  }

  //console.log(Message.Title.ACCESS_TOKEN, accessToken);

  if (Utility.isValid.AccessToken(accessToken)) {
    
    ////console.log(Message.Title.TOKEN_EXPIRED);

    const refreshToken = Utility.Storage.Get(Code.LocalKey.RefreshToken);

    if (!refreshToken) {
      console.error(Message.Title.TOKEN_NOT_FOUND);
      throw new Error(Message.Title.TOKEN_NOT_FOUND); 
    }

    if(Utility.isValid.RefreshToken(refreshToken))
    {
      //navigate('/login');
    }

    accessToken = await generateAccessToken(refreshToken);

    localStorage.setItem(Code.LocalKey.AccessToken, accessToken);
 
  } else {
      ////console.log(Message.Title.TOKEN_VALID);
  }
  return accessToken;
 
};

//generate new access token
const generateAccessToken = async (refreshToken: string | null): Promise<string> => {
  try {
      //console.log(Message.Title.REFRESH_TOKEN, refreshToken);

      const response = await fetch(Code.Path.SERVER +'auth/GenerateAccessToken', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorMsg = `${Message.Title.ERROR_STATUS} ${response.status}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const data = await response.json();
      return data.accessToken;
  } catch (error) {
      return Promise.reject(error);
  }
};


//get data by keyId
const getByKeyId = async <T>(
  url: string,
  keyId: string | number | undefined,
  setLoading: (loading: boolean) => void,
  setError: (error: string) => void
): Promise<T | null> => {
  //setLoading(true);
  setError("");
  if(!keyId) return null;

  if (url && keyId && keyId !== Code.Key.NEW) {
    try {

      const response = await apiClient.get(`${url}/${keyId}`);
      const result = response.data;

      //console.log("result------->",result);

      if (result.status === "success" && result.code === 200) {
        return result.data as T;
      } else {
        setError(result.message || "Unexpected response format");
        return null;
      }
    } catch (error: any) {
      let errorMsg =
        error.response?.data?.message ||
        `${Message.Title.ERROR_STATUS} ${error.response?.status}` ||
        error.message;
      setError(errorMsg);
      return null;
    } finally {
      //setLoading(false);
    }
  } else {
    //setLoading(false);
    return null;
  }
};

const getDataById = async <T>(formName: string, id: string | number | undefined): Promise<T | null> => {
  if (!formName || !id) return null; // Ensure ID is valid
  try {
    let path = `${formName}/${id}`;
    //console.log("----path---->",path);

    const response = await apiClient.get(path);
    const result = response.data;

    //console.log("----result---->",result);

      if (result.status === "success" && result.code === 200) {
        return result.data as T;
      } else {
        return null;
      }
  } catch (error) {
    console.error(`Error fetching data for ID: ${id}`, error);
    return null;
  }
};

const getLastDataById = async <T>(formName: string, id: string | number | undefined): Promise<T | null> => {
  if (!formName || !id) return null; // Ensure ID is valid
  try {
    let path = `${formName}/${id}`;
  
    const response = await apiClient.get(path);
    const result = response.data;

    if (result.status === "success" && result.code === 200) {
      return result.data as T;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error fetching data for ID: ${id}`, error);
    return null;
  }
};

const getProductViewById = async <T>(id: string | number | undefined): Promise<T | null> => {
  if ( !id) return null; // Ensure ID is valid
  try {
    let path = `custom/product/${id}`;
    //console.log("----path---->",path);

    const response = await apiClient.get(path);
    const result = response.data;

    //console.log("----result---->",result);

      if (result.status === "success" && result.code === 200) {
        return result.data as T;
      } else {
        return null;
      }
  } catch (error) {
    console.error(`Error fetching data for ID: ${id}`, error);
    return null;
  }
};

const getInventoryViewById = async <T>(skuUnitCode: string | undefined): Promise<T | null> => {
  if ( !skuUnitCode) return null;
  try {
    let path = `custom/inventory/${skuUnitCode}`;
    //console.log("----path---->",path);

    const response = await apiClient.get(path);
    const result = response.data;

    //console.log("----result---->",result);

      if (result.status === "success" && result.code === 200) {
        return result.data as T;
      } else {
        return null;
      }
  } catch (error) {
    console.error(`Error fetching data for SKU unit code: ${skuUnitCode}`, error);
    return null;
  }
};

const getDataBySearch = async <T>(formName: string, column: string, value: string, fields: string): Promise<T | null> => {
  if (!formName || !column || !value || !fields) return null; // Ensure ID is valid
  try {
    const response = await apiClient.get(`${formName}/search?fields=${fields}&column=${column}&value=${value}`);
    const result = response.data;

      if (result.status === "success" && result.code === 200) {
        return result.data as T;
      } else {
        return null;
      }
  } catch (error) {
    console.error(`Error fetching data for search: column=${column} value=${value}`, error);
    return null;
  }
};

const getDataByUrl = async <T>(url: string): Promise<T | null> => {
  if (!url) return null; // Ensure Url is valid
  try {
    const response = await apiClient.get<T>(url);
    return response.data || null;
  } catch (error) {
    console.error(`Error fetching data by Url:`, error);
    return null;
  }
};

const getClientNameById = async (id: number | undefined): Promise<string> => {
  if (!id) return "";

  try {
    const response = await apiClient.get(`client/${id}`);

    if (!response.data) return "";

    return Utility.Get.ClientName(response.data.data, Code.Format.Client.ADDRESS_PHONE);
   
  } catch (error) {
    console.error("Error fetching client details:", error);
    return "";
  }
};

interface ClientInfo {
  client_name: string;
  client_number: string;
}
const getClientNameNumber = async (clientId: string | number | undefined): Promise<Partial<ClientInfo>> => {
  if (!clientId) return {};

  try {
    const client = await API.Data.GetClient(clientId);
    if(client){
      const clientName = Utility.Get.ClientName(client, Code.Format.Client.ADDRESS_PHONE);
      const clientNumber = client?.client_number || '';

      const mappedData: Partial<ClientInfo> = {
        client_name: clientName, 
        client_number: clientNumber,
      };
      
      return mappedData;
    }
    return {};
  } catch (error) {
    console.error("Error fetching client details:", error);
    return {};
  }
};

const getProductNameById = async (id: string | number | undefined): Promise<string> => {
  if (!id) return "";

  try {
    const response = await apiClient.get(`product/${id}`);
    if (!response.data) return '';
    let name = `${response.data.data.product_name || ''}`;
    return name.trim();
   
  } catch (error) {
    console.error("Error fetching client details:", error);
    return "";
  }
};

async function getSpecificationId(productId: number | undefined): Promise<number> {
  if (!productId) return 0;

  try {
    const product = await API.Data.GetById<Product>(Code.Form.Name.PRODUCT, productId);
    const categoryId = product?.category_id;
    if (!categoryId) return 0;

    const category = await API.Data.GetById<Category>(Code.Form.Name.CATEGORY, categoryId);
    const specificationId = category?.specification_id;
    return specificationId ?? 0;
  } catch (error) {
    console.error("getSpecificationId error:", error);
    return 0;
  }
}


export const getCompanyData = async (companyId: string | number): Promise<Company | null> => {
  if (!companyId) return null;

  try {
    const company = await API.Data.GetById<Company>(Code.Form.Name.COMPANY, companyId);
    return company;
  } catch (error) {
    console.error("Error fetching company data:", error);
    return null;
  }
};

export const getLocationData = async (locationId: number | undefined): Promise<Location | null> => {
  if (!locationId) return null;

  try {
    const location = await API.Data.GetById<Location>(Code.Form.Name.LOCATION, locationId);
    return location;
  } catch (error) {
    console.error("Error fetching location data:", error);
    return null;
  }
};

export const getLocationCityData = async (locationId: number | undefined): Promise<Location | null> => {
  if (!locationId) return null;

  try {
    const location = await API.Data.GetById<Location>(Code.Form.Name.LOCATION, locationId);
    if (!location) return null;

    // Fetch city data if client has a city_id
    let cityName: string | undefined;
    if (location.city_id) {
      const city = await API.Data.GetById<City>(Code.Form.Name.CITY, location.city_id);
      cityName = city?.city_name;
    }

    // Return updated location data with city_name
    return { ...location, city_name: cityName };

  } catch (error) {
    console.error("Error fetching location data:", error);
    return null;
  }
};

export const getLastInsuranceData = async (clientId: number | undefined): Promise<Insurance | null> => {
  if (!clientId) return null;

  try {
    const location = await API.Data.GetById<Location>(Code.Form.Name.LOCATION, clientId);
    return location;
  } catch (error) {
    console.error("Error fetching location data:", error);
    return null;
  }
};

// export const getProductView = async (productId: string | number | undefined): Promise<Product | null> => {
//   if (!productId) return null;

//   try {
//     // Fetch product data
//     const product = await API.Data.GetProductView(productId);
//     if (!product) return null;

//     // Return updated product data with city_name
//     return product;
//   } catch (error) {
//     console.error("Error fetching product data:", error);
//     return null;
//   }
// };

export const getClientData = async (clientId: string | number | undefined): Promise<Client | null> => {
  if (!clientId) return null;

  try {
    // Fetch client data
    const client = await API.Data.GetById<Client>(Code.Form.Name.CLIENT, clientId);
    if (!client) return null;

    // Return updated client data with city_name
    return client;
  } catch (error) {
    console.error("Error fetching client data:", error);
    return null;
  }
};

export const getProductOfferTaxData = async (
  productId: string | number | undefined
): Promise<[Product | null, Offer | null, Tax | null]> => {
  if (!productId) return [null, null, null];

  let product = null;
  let offer = null;
  let tax = null;

  try {
    // Fetch product data
    product = await API.Data.GetById<Product>(Code.Form.Name.PRODUCT, productId);
    if (product) {
      const offerId = product.offer_id || 0;
      if (offerId > 2) {
        offer = await API.Data.GetOffer(offerId);
      }

      const taxId = product.tax_id || 0;
      if (taxId > 2) {
        tax = await API.Data.GetTax(taxId);
      }
    }

    return [product, offer, tax];
  } catch (error) {
    console.error("Error fetching product data:", error);
    return [null, null, null];
  }
};

export const getProductData = async (productId: string | number | undefined): Promise<Product | null> => {
  if (!productId) return null;

  try {
    // Fetch product data
    const product = await API.Data.GetById<Product>(Code.Form.Name.PRODUCT, productId);
    if (!product) return null;

    // Return updated product data with city_name
    return product;
  } catch (error) {
    console.error("Error fetching product data:", error);
    return null;
  }
};

export const getOfferData = async (offerId: string | number | undefined): Promise<Offer | null> => {
  if (!offerId) return null;

  try {
    // Fetch offer data
    const offer = await API.Data.GetById<Offer>(Code.Form.Name.OFFER, offerId);
    if (!offer) return null;

    // Return updated offer data with city_name
    return offer;
  } catch (error) {
    console.error("Error fetching offer data:", error);
    return null;
  }
};

export const getTaxData = async (taxId: string | number | undefined): Promise<Tax | null> => {
  if (!taxId) return null;

  try {
    // Fetch tax data
    const tax = await API.Data.GetById<Tax>(Code.Form.Name.TAX, taxId);
    if (!tax) return null;

    // Return updated tax data with city_name
    return tax;
  } catch (error) {
    console.error("Error fetching tax data:", error);
    return null;
  }
};

export const getInventoryData = async (inventoryId: string | number | undefined): Promise<Inventory | null> => {
  if (!inventoryId) return null;

  try {
    // Fetch inventory data
    const inventory = await API.Data.GetById<Inventory>(Code.Form.Name.INVENTORY, inventoryId);
    if (!inventory) return null;

    // Return updated client data with city_name
    return inventory;
  } catch (error) {
    console.error("Error fetching inventory data:", error);
    return null;
  }
};

export const getInventoryBySearch = async (column: string, value: string, fields: string): Promise<Inventory | null> => {
  if (!column || !value || !fields) return null;

  try {
    // Fetch inventory data
    const inventory = await API.Data.GetBySearch<Inventory>(Code.Form.Name.INVENTORY, column, value, fields);
    if (!inventory) return null;

    // Return updated client data with city_name
    return inventory;
  } catch (error) {
    console.error("Error fetching inventory by search:", error);
    return null;
  }
};


export const getClientCityData = async (clientId: string | number | undefined): Promise<Client | null> => {
  if (!clientId) return null;

  try {
    // Fetch client data
    const client = await API.Data.GetById<Client>(Code.Form.Name.CLIENT, String(clientId));
    if (!client) return null;

    // Fetch city data if client has a city_id
    let cityName: string | undefined;
    if (client.city_id) {
      const city = await API.Data.GetById<City>(Code.Form.Name.CITY, client.city_id);
      cityName = city?.city_name;
    }

    // Return updated client data with city_name
    return { ...client, city_name: cityName };
  } catch (error) {
    console.error("Error fetching client data:", error);
    return null;
  }
};



export const getDoctorData = async (doctorId: string | number | undefined): Promise<Doctor | null> => {
  if (!doctorId) return null;

  try {
    const doctor = await API.Data.GetById<Doctor>(Code.Form.Name.DOCTOR, doctorId);
    return doctor;
  } catch (error) {
    console.error("Error fetching doctor data:", error);
    return null;
  }
};

export const getRecDoctorData = async (recDoctorId: string | number | undefined): Promise<RecDoctor | null> => {
  if (!recDoctorId) return null;

  try {
    const recdoctor = await API.Data.GetById<RecDoctor>(Code.Form.Name.RECDOCTOR, recDoctorId);
    return recdoctor;
  } catch (error) {
    console.error("Error fetching Receiving doctor data:", error);
    return null;
  }
};

export const getDoctorSubmitterData = async (doctorId: string | number | undefined): Promise<Doctor | null> => {
  if (!doctorId) return null;

  try {
    // Fetch client data
    const doctor = await API.Data.GetById<Doctor>(Code.Form.Name.DOCTOR, String(doctorId));
    if (!doctor || !doctor.submitter_id) return null;

    

    const submitter = await API.Data.GetById<Submitter>(Code.Form.Name.SUBMITTER, doctor.submitter_id);
    if (!submitter) return null;

    //console.log("----submitter---->",submitter);

    return { 
      ...doctor, 
      ...submitter 
    };
    
  } catch (error) {
    console.error("Error fetching client data:", error);
    return null;
  }
};

export const getDoctorName = async (
  doctorId: string | null,
  setDoctorName: (doctorName: string) => void,
) => {
  if (!doctorId) return '';

  try {
    const doctor = await API.Data.GetById<Doctor>(Code.Form.Name.DOCTOR, doctorId);
    if (!doctor) return '';

    const { doctor_first_name = '', doctor_middle_name = '', doctor_last_name = '' } = doctor;
    setDoctorName(`${doctor_first_name} ${doctor_middle_name} ${doctor_last_name}`.trim());
  } catch (error) {
    console.error("Error fetching doctor data:", error);
    setDoctorName('');
  }
};

export const getUserData = async (userId: string | number | undefined): Promise<User | null> => {
  if (!userId) return null;

  try {
    const user = await API.Data.GetById<User>(Code.Form.Name.USER, userId);
    return user;
  } catch (error) {
    console.error("Error fetching user data:", error);
    return null;
  }
};

export const getInsurerData = async (insurerId: number | undefined): Promise<Insurer | null> => {
  if (!insurerId || insurerId<10) return null;
 
  try {
    const insurer = await API.Data.GetById<Insurer>(Code.Form.Name.INSURER, insurerId);
    return insurer;
  } catch (error) {
    console.error("Error fetching insurer data:", error);
    return null;
  }
};

export const getTermData = async (termId: number | undefined): Promise<string> => {
  if (!termId) return '';

  try {
    const term = await API.Data.GetById<Term>(Code.Form.Name.TERM, termId);
    return term?.term_comment || '';
  } catch (error) {
    console.error("Error fetching term data:", error);
    return '';
  }
};

export const getCityData = async (cityId: string | number): Promise<City | null> => {
  if (!cityId) return null;

  try {
    const city = await API.Data.GetById<City>(Code.Form.Name.USER, cityId);
    return city;
  } catch (error) {
    console.error("Error fetching city data:", error);
    return null;
  }
};

const sendMessage = async (
  clientId: number | undefined,
  locationId: number | undefined,
  segmentId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const data = new FormData();
    if (clientId) data.append("client_id", clientId.toString());
    if (locationId) data.append("location_id", locationId.toString());
    if (segmentId) data.append("segment_id", segmentId.toString());

    // Debug FormData contents
    const entries = Array.from(data.entries());
    entries.forEach(([key, value]) => {
      //console.log(key, value);
    });

    const url = "custom/send/message";
    const response = await apiClient.post(url, data);

    //console.log("---response--->", response);

    const result = response.data;
    if (result.status === "success" && result.code === 200) {
      Utility.Message.Info([`Message sent successfully`], setMessage);
    }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const sendNotification = async (
  companyId: number | undefined,
  segmentId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const data = new FormData();
    if (companyId) data.append("company_id", companyId.toString());
    if (segmentId) data.append("segment_id", segmentId.toString());

    // Debug FormData contents
    const entries = Array.from(data.entries());
    entries.forEach(([key, value]) => {
      //console.log(key, value);
    });

    const url = "custom/send/notification";
    const response = await apiClient.post(url, data);

    //console.log("---response--->", response);

    const result = response.data;
    if (result.status === "success" && result.code === 200) {
      Utility.Message.Info([`Message sent successfully`], setMessage);
    }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const updateSaleorder = async (
  saleOrderId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const url = `custom/saleorder/${saleOrderId}`;
    const response = await apiClient.put(url);

    //console.log("updateSaleorder---response--->", response);

    const result = response.data;
    // if (result.status === "success" && result.code === 200) {
    //   Utility.Message.Info([`Sale order updated successfully`], setMessage);
    // }

    return result;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const generateForm = async (
  saleOrderId: number | undefined,
  identity: string,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const url = `custom/generateform/${saleOrderId}/${identity}`;
    const response = await apiClient.get(url);

    //console.log("updateSaleorder---response--->", response);

    const result = response.data;
    // if (result.status === "success" && result.code === 200) {
    //   Utility.Message.Info([`Sale order updated successfully`], setMessage);
    // }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const verifyPurchase = async (
  purchaseId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const url = `custom/verify/purchase/${purchaseId}`;
    const response = await apiClient.get(url, { timeout: 60000 });

    //console.log("verifyPurchase---response--->", response);

    const result = response.data;
    // if (result.status === "success" && result.code === 200) {
    //   Utility.Message.Info([`Sale order updated successfully`], setMessage);
    // }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const activateExamGroup = async (
  examId: number | undefined,
  groupId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const url = `exam/activate/${examId}/${groupId}`;
    const response = await apiClient.get(url);

    //console.log("activate---exam--->", response);

    if (response.status === 200) {
      Utility.Message.Info([`Exam ${groupId} avtivated`], setMessage);
    }

    return response;
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

const callGetApi = async (
  url: string,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const response = await apiClient.get(url);
    const result = response.data;

    if (result.status === "success" && result.code === 200) {
      Utility.Message.Info(["Transaction successful"], setMessage);
    } else {
      Utility.Message.Warning(
        [result.message || "Transaction failed"],
        setMessage
      );
    }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
    return null; // optional, but recommended
  }
};

const callPostApi = async (
  url: string,
  payload: any,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const response = await apiClient.post(url, payload);
    const result = response.data;

    if (result.status === "success" && result.code === 200) {
      Utility.Message.Info(["Transaction successful"], setMessage);
    } else {
      Utility.Message.Warning(
        [result.message || "Transaction failed"],
        setMessage
      );
    }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
    return null;
  }
};

const callPutApi = async (
  url: string,
  payload: any,
  setMessage: Dispatch<SetStateAction<MessageType>>
): Promise<any> => {
  try {
    const response = await apiClient.put(url, payload);
    const result = response.data;

    if (result.status === "success" && result.code === 200) {
      Utility.Message.Info(["Transaction successful"], setMessage);
    } else {
      Utility.Message.Warning(
        [result.message || "Transaction failed"],
        setMessage
      );
    }

    return result.data;
  } catch (error) {
    Utility.Handle.Error(error);
    return null;
  }
};


//save record to the server
const saveRecord = async (
  url: string, 
  keyId: string | number | undefined, 
  data: object, 
  setMessage: Dispatch<SetStateAction<MessageType>>,
  Animate: boolean = true,
  popupClose: number = Code.Form.PopupClose.ALWAYS,
  redirectUrl:string ='',
): Promise<any> => {
  try {
    if(Animate) await Utility.Animate.Save.Start();
    const formName = Utility.Get.FormFullName(url);
    let response = null;
    let result;

    //console.log("Save Request URL:", url);
    //console.log("Save Request Data:", data);

    if (keyId === Code.Key.NEW) {
      response = await apiClient.post(url, data);
      result = response.data;

      if(Animate) {
        if (result.status === "success" && result.code === 200) {
          Utility.Message.Info([`New ${formName.toLowerCase()} created successfully`],setMessage);
        }
        
        await new Promise((resolve) => setTimeout(resolve, 1000));
        //console.log("SaveRecord response:", response.data);
        Utility.Animate.Save.End();
      }
      
      
      if (popupClose == Code.Form.PopupClose.ALWAYS || popupClose == Code.Form.PopupClose.ONCREATE) // 1. close the popup everytime, 2. close the popup when the record is created.
      {
        Utility.Button.PopupClose();
      } 
      else if (popupClose == Code.Form.PopupClose.REDIRECT) // popup remain open for create and update.
      {
        if (result.data) 
        {
          const newKey = result.data[Code.Key.NEWID];
          if (newKey) 
          {
            if(redirectUrl.includes(Code.Key.NEWID)) {
              redirectUrl = redirectUrl.replace(Code.Key.NEWID,newKey);
            }
            window.location.href = `${redirectUrl}`;
          }
          else Utility.Button.PopupClose();
        }
      }
    } 
    else 
    {
      url = `${url}/${keyId}`;
      response = await apiClient.put(url, data);
      result = response.data;
      if(Animate) {
        if (result.status === "success" && result.code === 200) {
          Utility.Message.Info([`${formName} updated successfully`],setMessage);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
        Utility.Animate.Save.End();
      }
      if (popupClose == Code.Form.PopupClose.ALWAYS) Utility.Button.PopupClose(); 
    }

    return result ?? null; // ✅ returning the response data here

  } catch (error) {
    Utility.Handle.Error(error);
  }
};

//save record to the server
const saveItemInventory = async (
  ItemId: string | undefined, 
  InventoryId: string | undefined, 
  itemData: Item, 
  inventoryData: Inventory, 
  popupClose: number = Code.Form.PopupClose.ALWAYS,
): Promise<any> => {
  try {
    let response = null;
    const url = 'iteminventory';

    const itemInventoryGroup: ItemInventoryGroup = {
      item: itemData,
      inventory: inventoryData,
      parameter: undefined, // optional if needed, remove if not needed
    };

    //console.log("itemInventoryData:",itemInventoryGroup);

    response = await apiClient.post(url, itemInventoryGroup);
    const result = response.data;
    if (result) {
      const itemId = result.data['itemid'];
      const inventoryId = result.data['inventoryid'];
      const utncode = result.data['utncode'];
      const skucode = result.data['skucode'];
      if (itemId && inventoryId)  {
        //console.log("SaveItemInventory:", itemId, inventoryId, utncode, skucode);
      }
    }

    if (popupClose == Code.Form.PopupClose.ALWAYS || popupClose == Code.Form.PopupClose.ONCREATE) 
    {
      Utility.Button.PopupClose();
    } 
    

    // //ItemInventory API
    // if (ItemId === Code.Key.NEW) {

    //   response = await apiClient.post(url, itemInventoryGroup);
    //   const result = response.data;
    //   if (result) {
    //     const itemId = result.data['itemid'];
    //     const inventoryId = result.data['inventoryid'];
    //     const utncode = result.data['utncode'];
    //     const skucode = result.data['skucode'];
    //     if (itemId && inventoryId)  {
    //       //console.log("SaveItemInventory:", itemId, inventoryId, utncode, skucode);
    //     }
    //   }
    // } 
    // else {
    //   if (InventoryId === Code.Key.NEW) {

    //   }
    //   else
    //   {
    //     response = await apiClient.put(`${url}/${ItemId}`, itemInventoryGroup);
    //     const result = response.data;
    //     if (result) {
    //       const itemId = response.data['itemid'];
    //       const inventoryId = response.data['inventoryid'];
    //       const skucode = response.data['skucode'];
    //       if (itemId && inventoryId && skucode)  {
    //         //console.log("SaveItemInventory:", itemId, inventoryId, skucode);
    //       }
    //     }
    //   }
    // }
  
    return response?.data; 

  } catch (error) {
    Utility.Handle.Error(error);
  }
};

// //save record to the server
// const saveItemInventory = async (
//   ItemId: string, 
//   InventoryId: string, 
//   itemData: Item, 
//   inventoryData: Inventory, 
//   SkuCode: boolean = false,
// ): Promise<any> => {
//   try {
//     let response = null;
//     let inventoryNewKeyId = null;
    
//     //Inventory API
//     if (InventoryId === Code.Key.NEW) {

//       if(SkuCode){
        
//       }

//       response = await apiClient.post("inventory", inventoryData);
      
//       if (response?.data) {
//         const newKeyId = response.data[`newid`];
//         if (newKeyId)  {
//           inventoryNewKeyId = newKeyId;
//           //console.log("SaveItemInventory: Inventory Created");
//         }
//       }
//     } 
//     else {
//       response = await apiClient.put(`inventory/${InventoryId}`, inventoryData);
//       //console.log("SaveItemInventory: Inventory Updated");
//     }

//     if (inventoryNewKeyId) {
//       const newid = Extract.Key.Decode(inventoryNewKeyId);
//       if(newid>0){
//         itemData.inventory_id = newid;
//       }
//     }

//     //Item API
//     if (ItemId === Code.Key.NEW) {
//       response = await apiClient.post("item", itemData);
      
//       if (response?.data) {
//         const newKeyId = response.data[`newid`];
//         if (newKeyId)  { 
//           //console.log("SaveItemInventory: Item Created");  
//         }
//       }
//     } 
//     else {
//       response = await apiClient.put(`item/${ItemId}`, itemData);
//       //console.log("SaveItemInventory: Item Updated");
//     }

//     return response?.data; 

//   } catch (error) {
//     Utility.Handle.Error(error);
//   }
// };


//delete or restore record from the server
const deleteRecord = async (
  form: string, 
  keyId: string, 
  ActiveId: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>,
  popupClose: number = Code.Form.PopupClose.ALWAYS
): Promise<void> => {
  try {
    
    await Utility.Animate.DeleteRecord.Start(); 

    let url = `${form}/${keyId}`;
    let status = ActiveId === Code.Option.Active.ACTIVE ? Code.Option.Active.DELETE : Code.Option.Active.ACTIVE;
    const data = { [`${form}_active`]: status };

    const response = await apiClient.put(url,data);

    Utility.Message.Warning([`${form} deleted successfully`],setMessage);

    await new Promise((resolve) => setTimeout(resolve, 500));
  
    //console.log("DeleteRecord:", response.data);

    Utility.Animate.DeleteRecord.End();
    if (popupClose == Code.Form.PopupClose.ALWAYS) Utility.Button.PopupClose();

  } catch (error) { Utility.Handle.Error(error); }
};

//delete or restore record from the server
const mergeClient = async (
  clientId1: number, 
  clientId2: number, 
  setMessage: Dispatch<SetStateAction<MessageType>>,
): Promise<any> => {
  try {
    const url = `custom/merge/${clientId1}/${clientId2}`;
    const response = await apiClient.get(url);

    Utility.Message.Confirmation(["Merged successfully"], setMessage);

    return response.data; // Return actual data
  } catch (error) {
    Utility.Handle.Error(error);
    throw error;
  }
};

//generate url to fetch data from the server 
const buildListApiEndpoint = (
    form: string,
    pno: number,
    rows: number,
    order: string,
    asc: number,
    active: number,
    searchKey: string,
    params: { [key: string]: any } = {}
  ): string => {
    let url = `${form}?`;
    if(rows>0) {
      url += `limit=${rows}`;
      if(pno>1) url += `&offset=${((pno-1)*rows)}`; 
    } 
    if(order) {
      url += `&orderby=${order}`;
      if(asc===Code.Option.Sort.DESC) url += `&asc=${Code.Option.Sort.DESC}`;
    }
    if (searchKey) url += `&search=${searchKey}`;
    if(active && active !== 2) url += `&${form}_active=${active}`;
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "" && value !== 1) {
        url += `&${key}=${value}`;
      }
    }
    return url; 
};

const buildFormApiEndpoint = (
  form: string,
  rows: number,
  order: string,
  asc: number,
  params: { [key: string]: any } = {}
): string => {
  let url = `${form}?`;
  if(rows>0) {
    url += `limit=${rows}`;
  } 
  if(order) {
    url += `&orderby=${order}`;
    if(asc===Code.Option.Sort.DESC) url += `&asc=${Code.Option.Sort.DESC}`;
  }
  url += `&${form}_active=${Code.Option.Active.ACTIVE}`;
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "" && value !== 1) {
      url += `&${key}=${value}`;
    }
  }
  return url; 
};

//generate url to fetch data from the server 
const buildCalendarApiEndpoint = (
    form: string,
    params: { [key: string]: any } = {}
  ): string => {
    let url = `${form}?`;
    url += `&${form}_active=2`;
    url += `&limit=200`;
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "" && value !== 1) {
        url += `&${key}=${value}`;
      }
    }
    return url; 
};

//generate url to fetch data from the server 
const buildReportApiEndpoint = (
    companyId:number,
    reportId: number,
    startDate: string,
    endDate: string,
    reportFilter: ReportFilter,
  ): string => {
    let url = '';
   if(companyId && reportId && startDate && endDate) {
      url += `custom/report?companyid=${companyId}&reportid=${reportId}&startdate=${startDate}&enddate=${endDate}`;
      if(reportFilter.field1 && reportFilter.value1) url += `&field1=${reportFilter.field1}&value1=${reportFilter.value1}`;
      if(reportFilter.field2 && reportFilter.value2) url += `&field2=${reportFilter.field2}&value2=${reportFilter.value2}`;
      if(reportFilter.field3 && reportFilter.value3) url += `&field3=${reportFilter.field3}&value3=${reportFilter.value3}`;
      if(reportFilter.field4 && reportFilter.value4) url += `&field4=${reportFilter.field4}&value4=${reportFilter.value4}`;
      if(reportFilter.field5 && reportFilter.value5) url += `&field5=${reportFilter.field5}&value5=${reportFilter.value5}`;
      if(reportFilter.field6 && reportFilter.value6) url += `&field6=${reportFilter.field6}&value6=${reportFilter.value6}`;
      if(reportFilter.field7 && reportFilter.value7) url += `&field7=${reportFilter.field7}&value7=${reportFilter.value7}`;
      if(reportFilter.field8 && reportFilter.value8) url += `&field8=${reportFilter.field8}&value8=${reportFilter.value8}`;
      if(reportFilter.field9 && reportFilter.value9) url += `&field9=${reportFilter.field9}&value9=${reportFilter.value9}`;
    } 
    return url; 
};

//Update batch record to the server
const UpdateBatchRecord = async (
  entity: string, 
  data: object, 
): Promise<any> => {
  try {
    let response = null;

    if (entity) {
      const url = `custom/batch/${entity}`;
      response = await apiClient.put(url, data);
  
      return response?.data; 
    }
  } catch (error) {
    Utility.Handle.Error(error);
  }
};

//function for changing the status of the record
const deleteRecords = async (
  formName: string,
  activeId: number,
  callListData: () => Promise<void>,
  selectedKeys: string[],
  setConfirm: (messages: MessageType) => void,
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setSelected: (selected: boolean) => void,
  setSelectedItems: (selectedItems: string[]) => void,
  setError: (error: string | null) => void,
  delay: number = 1 
): Promise<void> => {
  // Function to delay execution
  const delayExecution = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  try {

    await Utility.Animate.DeleteRecord.Start(); 

    for (const keyId of selectedKeys) {
      await deleteRecord(formName, keyId, activeId, setMessage); 
      await delayExecution(delay); 
    }

    Utility.Animate.DeleteRecord.End();

    await callListData(); 
    setConfirm(Code.Message.None); 
    setSelected(false); 
    setSelectedItems([]); 
    selectedKeys = [];
  } catch (error) {
    setError(error as string); // Handle errors
  }
};

const fetchDataForOptions = async (
  formName: string,
  first: number,
  dropFields: any,
  setData: (data: OptionType[]) => void,
  setError: (error: string) => void
) => {
  let fn = "SelectOptionDB:";

  try {
    const url = formName;

    // Call getAllRecords, which directly returns the data array
    const rawData: any[] = await API.Data.GetAll(`${url}?limit=200`);

    // Map the raw data to dropdown data
    const data: OptionType[] = Utility.Convert.DataToOption(rawData, dropFields.id, dropFields.name, Code.Option.Common.NOTHING);

    // Ensure the data is an array
    if (!Array.isArray(data) || data === null) {
      throw new Error(`${fn} Data is not an array`);
    }

    // Add a default option if specified
    let defaultOption: OptionType | undefined;
    if (first === Code.Option.Common.NA) defaultOption = { value: Code.Option.Common.NA, label: "N/A" };
    else if (first === Code.Option.Common.SELECT) defaultOption = { value: Code.Option.Common.SELECT, label: "Select" };
    else if (first === Code.Option.Common.ALL) defaultOption = { value: Code.Option.Common.ALL, label: "All" };
    else if (first === Code.Option.Common.NONE) defaultOption = { value: Code.Option.Common.NONE, label: "None" };
    else if (first === Code.Option.Common.GENERAL) defaultOption = { value: Code.Option.Common.GENERAL, label: "General" };

    // Add the default option to the dropdown data
    const optionData = defaultOption ? [defaultOption, ...data] : data;

    // Set the dropdown data
    setData(optionData);
  } catch (err) {
    if (err instanceof Error) {
      console.error(`${fn} Error loading role data:`, err.message);
      setError(err.message);
    } else {
      console.error(`${fn} Unexpected error:`, err);
      setError("An unexpected error occurred.");
    }
  }
};
const fetchAsyncOptions = async (
  inputValue: string,
  formName: string,
  valueColumn: string,
  labelColumn: string | string[] 
): Promise<OptionType[]> => {
  if (!inputValue) return [];

  try {
    if(inputValue.length<=1) return [];

    if(inputValue.includes('.')) inputValue = inputValue.replaceAll('.','');

    const url = `${Code.Path.SERVER}${formName}?search=${inputValue}&fields=${valueColumn},${labelColumn}`;
    const result = await getAllRecords(url);

    if (result.code && result.code !== Message.Code.SUCCESS) return []; 

    const rawData = result.data;

    const data: OptionType[] = Utility.Convert.DataToOption(rawData, valueColumn, labelColumn, Code.Option.Common.NOTHING);

    return data.map((item: any) => ({
      value: item.value,
      label: item.label,
    }));
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

// const fetchAsyncOptions = async (
//   inputValue: string,
//   formName: string,
//   valueColumn: string,
//   labelColumn: string | string[] 
// ): Promise<OptionType[]> => {
//   if (!inputValue) return [];

//   try {

//     if(inputValue.length<=1) return [];

//     const url = `${Code.Path.SERVER}${formName}?search=${inputValue}&fields=${valueColumn},${labelColumn}`;

//     //console.log("Fetching options for input:", url);
    
//     const response = await getAllRecords(url);
//     //const response = await fetch(url);

    

//     const result = await response.json();
//     const rawData = result.data;

//     //console.log("Fetching options for input:", rawData);

//     const data: OptionType[] = Utility.Convert.DataToOption(rawData, valueColumn, labelColumn, Code.Option.Common.NOTHING);

//     // Map the response data to match the OptionType
//     return data.map((item: any) => ({
//       value: item.value,
//       label: item.label,
//     }));
//   } catch (error) {
//     console.error("Error fetching data:", error);
//     return [];
//   }
// };

const fetchAsyncOptionsWithId = async (
  inputValue: string,
  formName: string,
  valueColumn: string,
  labelColumn: string | string[] 
): Promise<OptionType[]> => {
  if (!inputValue) return [];

  try {
    if(inputValue.length<=1) return [];

    const response = await fetch(`${Code.Path.SERVER}${formName}?search=${inputValue}&fields=${valueColumn},${labelColumn}`);
    const result = await response.json();
    const rawData = result.data;

    const data: OptionType[] = Utility.Convert.DataToOption(rawData, valueColumn, labelColumn, Code.Option.Common.SELECT);

    // Map the response data to match the OptionType
    return data.map((item: any) => ({
      value: item.value,
      label: item.label,
    }));
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

const getNewSerialNumber = async (
  form: string,
  companyId: number,
): Promise<number> => { 
  let url = `${form}?fields=${form}_serial&company_id=${companyId}&limit=1&orderby=${form}_serial&asc=${Code.Option.Sort.DESC}`;
  //console.log(`Generated new serial number url:`, url);
  const records = await getAllRecords(url);
  //console.log(`Generated new serial number data:`, records?.data[0],form);
  if (records) {
    const serial = Number(records?.data[0]?.[`${form}_serial`]) || 0;
    return serial + 1;
  }

  return 1;
};

const getSingleValue = async (
  form: string,
  selectField: string,
  whereField: string,
  whereValue: string,
): Promise<string> => { 
  let url = `${form}?fields=${selectField}&${whereField}=${whereValue}`;
  const records = await getAllRecords(url);
  const selectValue = records?.data[0]?.[selectField] ?? ''; 
  return selectValue; 
};

const AdditionalData = async (
  formData: any,
  formName: string,
  keyId: string,
  newSerialNumber: boolean = false
) => {

  let rawDate = formData[`${formName}_date`] || Utility.Date.Current();
  let dateObj = new Date(rawDate);

  dateObj = Utility.Date.setlocalDate(dateObj);

  const numericSuffixes = [
    '_amount',
    '_price',
    '_quantity',
    '_total',
    '_percentage',
    '_value',
    '_sale',
    '_stock',
    '_sorting',
    '_rate',
    '_count'
  ];

  Object.keys(formData).forEach((key) => {
    if (key.startsWith(`${formName}_`) && numericSuffixes.some(suffix => key.endsWith(suffix))) {
      if (formData[key] === '' || formData[key] === null) {
        formData[key] = 0;
      }
    }
  });

  console.log("formData.Items:", formData.items);

  if (formData.items && Array.isArray(formData.items)) {
    formData.items.forEach((item: any) => {
      Object.keys(item).forEach((key) => {
        if (key.startsWith('item_') && numericSuffixes.some(suffix => key.endsWith(suffix))) {
          if (item[key] === '' || item[key] === null) {
            console.log("Item Key:", key);
            item[key] = 0;
          }
        }
      });
    });
  }

  console.log("formData.Items2:", formData.items);



  let additionalData: Record<string, any> = {
    [`${formName}_date`]: dateObj.toISOString(),
  };

  // Only add company/location/user/serial info if it's a new entry
  if (keyId === Code.Key.NEW) {
    const companyId = Utility.Storage.CompanyId();
    const locationId = Utility.Storage.LocationId();
    const userId = Utility.Storage.UserId();
    let format = Code.Format.Sno.Common7;

    additionalData = {
      ...additionalData,
      company_id: formData.company_id || companyId,
      location_id: formData.location_id || locationId,
      user_id: formData.user_id || userId,
    };

    if (newSerialNumber) {
      const newSerial = await API.Data.NewSerialNumber(formName, companyId);
      if(formName==Code.Form.Name.BILLING){
          format = Code.Format.Sno.Common6;
      } 
      
      let newNumber = Utility.Format.SerialNumber(format, newSerial, locationId);

      if(formName==Code.Form.Name.CLIENT){
          newNumber = newSerial.toString();
      }

      additionalData = {
        ...additionalData,
        [`${formName}_serial`]: formData[`${formName}_serial`] || newSerial,
        [`${formName}_number`]: formData[`${formName}_number`] || newNumber,
      };
    }
  }

  return additionalData;
};

const saveToDocument = async (
  printRef: RefObject<HTMLDivElement>,
  formId: number,
  clientId: number | undefined,
  documentType: number | undefined,
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setError: (error: string) => void
) => {
  const pdfFile = await getPdfFile(printRef);
  const fileName = await Files.File.Attach(pdfFile, "document", setError);
  //console.log("Saved PDF File:", fileName);

  let documentData: Document = {
    document_date: new Date(),
    document_file_name: fileName,
    form_id: formId,
    client_id: clientId,
    company_id: Utility.Storage.CompanyId(),
    location_id: Utility.Storage.LocationId(),
    user_id: Utility.Storage.UserId(),
    type_id: documentType
  };

  const finalData = Utility.Sanitize.FormData({
    ...documentData,
  });

  await API.Data.Save(Code.Form.Name.DOCUMENT, Code.Key.NEW, finalData, setMessage)

};

const sendAsEmail = async (
  printRef: RefObject<HTMLDivElement>,
  email: string,
  subject: string,
  content: string,
  setMessage: Dispatch<SetStateAction<MessageType>>,
  setError: (error: string) => void
) => {
  const pdfFile = await getPdfFile(printRef);
  const response = await Files.File.Send(pdfFile,email,subject,content, setError);
  if(response){
    Utility.Message.Info([`Email sent successfully to ${email}`], setMessage);
  }

};


// const AdditionalData = async (
//   formData: any, 
//   formName: string, 
//   keyId: string, 
//   newSerialNumber: boolean = false
// ) => {
//   if (keyId !== Code.Key.NEW) return {}; // Return empty object if not a new entry

//   const companyId = Utility.Storage.CompanyId();
//   const locationId = Utility.Storage.LocationId();
//   const userId = Utility.Storage.UserId();

//   let additionalData: Record<string, any> = {
//     company_id: formData.company_id || companyId,
//     location_id: formData.location_id || locationId,
//     user_id: formData.user_id || userId,
//     [`${formName}_date`]: formData[`${formName}_date`] || Utility.Date.Current(),
//   };

//   if (newSerialNumber) {
//     const newSerial = await API.Data.NewSerialNumber(formName, companyId);
//     const newNumber = Utility.Format.SerialNumber(Code.Format.Sno.Payment, newSerial, companyId);

//     additionalData = {
//       ...additionalData,
//       [`${formName}_serial`]: formData[`${formName}_serial`] || newSerial,
//       [`${formName}_number`]: formData[`${formName}_number`] || newNumber,
//     };
//   }

//   return additionalData;
// };


export const API = {
  Data:{
    List: getDataList,
    return: returnDataList,
    GetAll: getAllRecords,
    GetByKeyId: getByKeyId,
    GetById: getDataById,
    GetLastById: getLastDataById,
    GetProductViewById: getProductViewById,
    GetInventoryViewById: getInventoryViewById,
    GetBySearch: getDataBySearch,
    GetByUrl: getDataByUrl,
    GetClientName: getClientNameById,
    getClientNameNumber: getClientNameNumber,
    GetProductName: getProductNameById,
    GetCompany: getCompanyData,
    GetLocation: getLocationData,
    GetLocationCity: getLocationCityData,
    GetInsurance: getLastInsuranceData,
    GetClient: getClientData,
    GetProductOfferTax: getProductOfferTaxData,
    GetProduct: getProductData,
    GetOffer: getOfferData,
    GetTax: getTaxData,
    GetInventory: getInventoryData,
    GetInventoryBySearch: getInventoryBySearch,
    GetClientCity: getClientCityData,
    GetDoctor: getDoctorData,
    GetDoctorSubmitter: getDoctorSubmitterData,
    GetRecDoctor: getRecDoctorData,
    GetDoctorName: getDoctorName,
    GetUser: getUserData,
    GetInsurer: getInsurerData,
    GetTerm: getTermData,
    GetCity: getCityData,
    NewSerialNumber: getNewSerialNumber,
    SingleValue: getSingleValue,
    Batch: UpdateBatchRecord,
    Save: saveRecord,
    sendMessage: sendMessage,
    sendNotification: sendNotification,
    SaveItemInventory: saveItemInventory,
    MergeClient: mergeClient,
    Delete: deleteRecord,
    MultiDelete: deleteRecords,
    AsyncOption: fetchAsyncOptions,
    OptionData: fetchDataForOptions,
    AddonData: AdditionalData,
    SpecificationId: getSpecificationId,
    saveToDocument: saveToDocument,
    sendAsEmail: sendAsEmail,
    updateSaleorder: updateSaleorder,
    generateForm: generateForm,
    verifyPurchase: verifyPurchase,
    activateExamGroup: activateExamGroup,
    callGetApi: callGetApi,
    callPostApi: callPostApi,
    callPutApi: callPutApi,
  },
  Token:{
    Access: getAccessToken,
    Refresh: generateAccessToken
  },
  Build:{
    ListApiEndpoint: buildListApiEndpoint,
    FormApiEndpint: buildFormApiEndpoint,
    CalendarApiEndpint: buildCalendarApiEndpoint,
    ReportApiEndpint: buildReportApiEndpoint
  }
  
}